import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Handles slideshow index updates and ensures only the active slide video plays.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    this.#syncActiveSlideVideo(event.detail.index);
  };

  /**
   * @param {number} activeIndex
   */
  #syncActiveSlideVideo(activeIndex) {
    const deferredMediaList = this.#getSlideshowDeferredMedia();
    if (deferredMediaList.length === 0) return;

    for (const { slideIndex, deferredMedia } of deferredMediaList) {
      if (slideIndex === activeIndex) {
        setTimeout(() => this.#playDeferredMedia(deferredMedia), 500);
      } else {
        deferredMedia.pauseMedia?.();
      }
    }
  }

  #getSlideshowDeferredMedia() {
    const slides = this.refs.slideshow?.querySelectorAll('slideshow-slide');
    if (!slides?.length) return [];

    /**
     * @typedef {{ slideIndex: number, deferredMedia: HTMLElement }} SlideDeferredMedia
     */
    /** @type {SlideDeferredMedia[]} */
    const deferredMediaList = [];

    for (const [index, slide] of slides.entries()) {
      const deferredMedia = slide.querySelector('deferred-media');
      if (deferredMedia instanceof HTMLElement) {
        const slideIndex = Number(slide.getAttribute('data-index'));
        deferredMediaList.push({ slideIndex, deferredMedia });
      }
    }

    return deferredMediaList;
  }

  /**
   * @param {HTMLElement} deferredMedia
   */
  #playDeferredMedia(deferredMedia) {
    const hasRenderedMedia = deferredMedia.querySelector('video, iframe[data-video-type]') !== null;

    if (!hasRenderedMedia && typeof deferredMedia.showDeferredMedia === 'function') {
      deferredMedia.showDeferredMedia();
      return;
    }

    if (typeof deferredMedia.playMedia === 'function') {
      deferredMedia.playMedia();
      return;
    }

    const video = deferredMedia.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    const playPromise = video.play();
    if (playPromise instanceof Promise) {
      playPromise.catch(() => {});
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
